import type { AudioEngine } from './AudioEngine';
import {
  BED_CROSSFADE_S,
  CUE_AMB_CRASH_WIND,
  CUE_AMB_FIRE,
  CUE_AMB_NIGHT,
  CUE_AMB_PULSE,
  CUE_AMB_SURF,
  FIRE_BED_GAIN,
  NIGHT_BED_GAIN,
  PULSE_BED_GAIN,
  SURF_BED_GAIN,
  WIND_BED_GAIN,
  synthesizeCrashWind,
  synthesizeFireBed,
  synthesizeNightBed,
  synthesizePulseBed,
  synthesizeSurf,
  warmCriticalBeds,
} from './Beds';

export type AmbSyncState = {
  night: boolean;
  fire: boolean;
  pulse: boolean;
};

type LoopVoice = {
  gain: GainNode;
};

/** Crash Coast beds + night / fire / pulse loops on the one graph. */
export class AmbDirector {
  readonly cues = {
    wind: CUE_AMB_CRASH_WIND,
    surf: CUE_AMB_SURF,
    night: CUE_AMB_NIGHT,
    fire: CUE_AMB_FIRE,
    pulse: CUE_AMB_PULSE,
  } as const;
  private started = false;
  private nightOn = false;
  private fireOn = false;
  private pulseOn = false;
  private night: LoopVoice | null = null;
  private fire: LoopVoice | null = null;
  private pulse: LoopVoice | null = null;

  get playing(): boolean {
    return this.started;
  }

  start(engine: AudioEngine): void {
    if (this.started || !engine.unlocked) {
      return;
    }
    this.started = true;
    const ctx = engine.ctx;
    const dest = engine.buses.amb;
    warmCriticalBeds(ctx);

    const windGain = ctx.createGain();
    const surfGain = ctx.createGain();
    windGain.gain.value = 0;
    surfGain.gain.value = 0;
    windGain.connect(dest);
    surfGain.connect(dest);

    const wind = ctx.createBufferSource();
    wind.buffer = synthesizeCrashWind(ctx);
    wind.loop = true;
    wind.connect(windGain);

    const surf = ctx.createBufferSource();
    surf.buffer = synthesizeSurf(ctx);
    surf.loop = true;
    surf.connect(surfGain);

    const now = ctx.currentTime;
    const end = now + BED_CROSSFADE_S;
    windGain.gain.setValueAtTime(0, now);
    surfGain.gain.setValueAtTime(0, now);
    windGain.gain.linearRampToValueAtTime(WIND_BED_GAIN, end);
    surfGain.gain.linearRampToValueAtTime(SURF_BED_GAIN, end);

    wind.start();
    surf.start();
    engine.setPlaceholder(true);
  }

  sync(engine: AudioEngine, state: AmbSyncState): void {
    if (!engine.unlocked) {
      return;
    }
    const wantNight = state.night && !state.pulse;
    const wantFire = state.fire;
    const wantPulse = state.pulse;
    if (wantNight !== this.nightOn) {
      this.night = this.night ?? startLoop(engine, synthesizeNightBed(engine.ctx), engine.buses.amb);
      fade(this.night.gain, engine.ctx, wantNight ? NIGHT_BED_GAIN : 0, wantPulse ? 0.12 : BED_CROSSFADE_S);
      this.nightOn = wantNight;
    }
    if (wantFire !== this.fireOn) {
      this.fire = this.fire ?? startLoop(engine, synthesizeFireBed(engine.ctx), engine.buses.sfx);
      fade(this.fire.gain, engine.ctx, wantFire ? FIRE_BED_GAIN : 0, 0.45);
      this.fireOn = wantFire;
    }
    if (wantPulse !== this.pulseOn) {
      this.pulse = this.pulse ?? startLoop(engine, synthesizePulseBed(engine.ctx), engine.buses.sfx);
      fade(this.pulse.gain, engine.ctx, wantPulse ? PULSE_BED_GAIN : 0, wantPulse ? 0.18 : 0.8);
      this.pulseOn = wantPulse;
      engine.buses.applyPulseDuck(wantPulse, engine.ctx.currentTime);
    }
  }
}

function startLoop(engine: AudioEngine, buffer: AudioBuffer, dest: AudioNode): LoopVoice {
  const gain = engine.ctx.createGain();
  gain.gain.value = 0;
  gain.connect(dest);
  const src = engine.ctx.createBufferSource();
  src.buffer = buffer;
  src.loop = true;
  src.connect(gain);
  src.start();
  return { gain };
}

function fade(gain: GainNode, ctx: BaseAudioContext, value: number, seconds: number): void {
  const now = ctx.currentTime;
  const current = gain.gain.value;
  gain.gain.cancelScheduledValues(now);
  gain.gain.setValueAtTime(current, now);
  gain.gain.linearRampToValueAtTime(value, now + Math.max(0.02, seconds));
}

export const amb = new AmbDirector();
